/* assets/component-cart-promos.js */

class CartDrawerPromos {
  constructor() {
    this.isSyncing = false;
    this.isInitializing = false;
    this.pendingInit = false;
    this.selectedBigGiftVariantId = '';
    this.progressAnimationFrames = {};
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.handleCartUpdated = this.handleCartUpdated.bind(this);

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    document.addEventListener('cart:updated', this.handleCartUpdated);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      this.init();
    }
  }

  handleCartUpdated() {
    if (this.isInitializing) {
      this.pendingInit = true;
      return;
    }

    if (this.__initRaf) {
      cancelAnimationFrame(this.__initRaf);
    }

    this.__initRaf = requestAnimationFrame(() => {
      this.__initRaf = null;
      this.init();
    });
  }

  bindElements() {
    this.root = document.querySelector('[data-js-cart-promos]');
    if (!this.root || this.root.dataset.enabled !== 'true') return false;

    this.barEl = this.root.querySelector('[data-js-cart-promo-bar]');
    this.messageEl = this.root.querySelector('[data-js-cart-promo-message]');
    this.progressEls = {
      shipping: this.root.querySelector('[data-js-cart-promo-progress="shipping"]'),
      gift: this.root.querySelector('[data-js-cart-promo-progress="gift"]'),
      big: this.root.querySelector('[data-js-cart-promo-progress="big"]')
    };
    this.stepEls = {
      shipping: this.root.querySelector('[data-step-key="shipping"]'),
      gift: this.root.querySelector('[data-step-key="gift"]'),
      big: this.root.querySelector('[data-step-key="big"]')
    };
    this.rewardsEl = document.querySelector('[data-js-cart-promo-rewards]');
    this.shippingRewardEl = document.querySelector('[data-js-cart-promo-reward-shipping]');

    this.popupEl = document.querySelector('[data-js-cart-promo-popup]');
    this.popupDialogEl = this.popupEl ? this.popupEl.querySelector('[data-js-cart-promo-dialog]') : null;
    this.confirmButtonEl = this.popupEl ? this.popupEl.querySelector('[data-js-cart-big-gift-confirm]') : null;
    this.popupOptionEls = this.popupEl
      ? Array.from(this.popupEl.querySelectorAll('[data-js-cart-big-gift-option]'))
      : [];

    this.config = this.getConfig();

    return true;
  }

  async init() {
    if (!this.bindElements()) return;

    this.isInitializing = true;

    try {
      if (this.confirmButtonEl) {
        this.confirmButtonEl.disabled = !this.selectedBigGiftVariantId;
      }

      this.hydrateFromCache();
      await this.sync();
    } finally {
      this.isInitializing = false;

      if (this.pendingInit) {
        this.pendingInit = false;
        this.init();
      }
    }
  }

  getCache() {
    window.__cartDrawerPromosCache = window.__cartDrawerPromosCache || {};
    return window.__cartDrawerPromosCache;
  }

  hydrateFromCache() {
    const cache = this.getCache();

    if (!cache || !cache.progress || !this.root) return;

    this.root.dataset.state = cache.state || this.root.dataset.state || '';

    if (this.barEl) {
      this.barEl.classList.toggle('is-clickable', cache.state === 'select_big_gift');
    }

    if (this.messageEl && typeof cache.message === 'string') {
      this.messageEl.textContent = cache.message;
      this.messageEl.classList.toggle('is-clickable', cache.state === 'select_big_gift');
      this.messageEl.disabled = cache.state !== 'select_big_gift';
      this.messageEl.setAttribute('aria-disabled', cache.state === 'select_big_gift' ? 'false' : 'true');
    }

    Object.entries(this.progressEls).forEach(([key, el]) => {
      if (!el) return;
      this.setProgressValue(el, cache.progress[key]);
    });

    Object.entries(this.stepEls).forEach(([key, el]) => {
      if (!el) return;
      const stepCache = cache.steps && cache.steps[key] ? cache.steps[key] : null;
      el.classList.toggle('is-active', !!(stepCache && stepCache.active));
      el.classList.toggle('is-complete', !!(stepCache && stepCache.complete));
      el.classList.toggle('is-current', !!(stepCache && stepCache.current));
    });

    if (this.rewardsEl && typeof cache.shippingReached === 'boolean') {
      this.rewardsEl.hidden = !cache.shippingReached;
    }

    if (this.shippingRewardEl && typeof cache.shippingReached === 'boolean') {
      this.shippingRewardEl.hidden = !cache.shippingReached;
    }
  }

  animateProgress(progressKey, progressEl, nextValue) {
    if (!progressEl) return;

    const target = Math.max(0, Math.min(100, Number(nextValue) || 0));
    const current = Math.max(
      0,
      Math.min(100, Number(progressEl.value) || Number(progressEl.getAttribute('value')) || 0)
    );

    if (this.progressAnimationFrames[progressKey]) {
      cancelAnimationFrame(this.progressAnimationFrames[progressKey]);
      delete this.progressAnimationFrames[progressKey];
    }

    if (Math.abs(target - current) < 0.01) {
      this.setProgressValue(progressEl, target);
      return;
    }

    const duration = 220;
    const startAt = performance.now();
    const delta = target - current;

    const tick = now => {
      const elapsed = now - startAt;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = current + delta * eased;

      this.setProgressValue(progressEl, value);

      if (progress < 1) {
        this.progressAnimationFrames[progressKey] = requestAnimationFrame(tick);
      } else {
        delete this.progressAnimationFrames[progressKey];
      }
    };

    this.progressAnimationFrames[progressKey] = requestAnimationFrame(tick);
  }

  saveRenderCache(payload) {
    window.__cartDrawerPromosCache = {
      ...(window.__cartDrawerPromosCache || {}),
      ...payload
    };
  }

  getRate() { const r = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1); return isFinite(r) && r > 0 ? r : 1; }
  getConfig() {
    const thresholds = {
      shipping: Math.round(this.toNumber(this.root.dataset.thresholdShipping) * this.getRate()), gift: Math.round(this.toNumber(this.root.dataset.thresholdGift) * this.getRate()), bigGift: Math.round(this.toNumber(this.root.dataset.thresholdBigGift) * this.getRate())
    };

    if (thresholds.gift < thresholds.shipping) thresholds.gift = thresholds.shipping;
    if (thresholds.bigGift < thresholds.gift) thresholds.bigGift = thresholds.gift;

    return {
      currencyCode: this.root.dataset.currencyCode,
      thresholds,
      freeGiftVariantId: (this.root.dataset.freeGiftVariantId || '').trim(),
      textSpendShipping: this.root.dataset.textSpendShipping,
      textSpendGift: this.root.dataset.textSpendGift,
      textSpendBigGift: this.root.dataset.textSpendBigGift,
      textSelectBigGift: this.root.dataset.textSelectBigGift,
      textComplete: this.root.dataset.textComplete
    };
  }

  toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  async fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to fetch cart');
    return response.json();
  }

  getPromoType(item) {
    if (!item || !item.properties) return '';
    return item.properties._cart_promo || '';
  }

  summarizeCart(cart) {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    const freeGiftItems = items.filter(item => this.getPromoType(item) === 'free_gift');
    const bigGiftItems = items.filter(item => this.getPromoType(item) === 'big_free_gift');

    return {
      totalPrice: this.toNumber(cart && cart.total_price),
      freeGiftItems,
      bigGiftItems,
      hasFreeGift: freeGiftItems.length > 0,
      hasBigGift: bigGiftItems.length > 0
    };
  }

  async addVariant(variantId, properties = {}) {
    if (!variantId) return;
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1, properties })
    });
  }

  async changeByKey(key, quantity) {
    if (!key) return;
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ id: key, quantity })
    });
  }

  async sync() {
    if (!this.root || this.isSyncing) return;
    this.isSyncing = true;

    try {
      let cart = await this.fetchCart();
      const changed = await this.syncGiftLines(cart);

      if (changed && typeof window.refreshCart === 'function') {
        await window.refreshCart({ openSidebar: false, notify: false });
        if (!this.bindElements()) return;
        this.hydrateFromCache();
        cart = await this.fetchCart();
      }

      this.render(cart);
    } catch (error) {
      console.error('[CartDrawerPromos]', error);
    } finally {
      this.isSyncing = false;
    }
  }

  async syncGiftLines(cart) {
    const summary = this.summarizeCart(cart);
    const { gift, bigGift } = this.config.thresholds;

    const shouldHaveFreeGift = summary.totalPrice >= gift && !!this.config.freeGiftVariantId;
    const shouldHaveBigGift = summary.totalPrice >= bigGift;

    let changed = false;

    if (!shouldHaveFreeGift && summary.freeGiftItems.length) {
      for (const item of summary.freeGiftItems) {
        await this.changeByKey(item.key, 0);
        changed = true;
      }
    }

    if (shouldHaveFreeGift && summary.freeGiftItems.length) {
      const [firstItem, ...restItems] = summary.freeGiftItems;

      if (firstItem.quantity !== 1) {
        await this.changeByKey(firstItem.key, 1);
        changed = true;
      }

      for (const item of restItems) {
        await this.changeByKey(item.key, 0);
        changed = true;
      }
    }

    if (shouldHaveFreeGift && summary.freeGiftItems.length === 0) {
      await this.addVariant(this.config.freeGiftVariantId, {
        _cart_promo: 'free_gift',
        _cart_promo_source: 'cart_drawer_promos'
      });
      changed = true;
    }

    if (!shouldHaveBigGift && summary.bigGiftItems.length) {
      for (const item of summary.bigGiftItems) {
        await this.changeByKey(item.key, 0);
        changed = true;
      }
    }

    if (shouldHaveBigGift && summary.bigGiftItems.length > 1) {
      const [firstItem, ...restItems] = summary.bigGiftItems;

      if (firstItem.quantity !== 1) {
        await this.changeByKey(firstItem.key, 1);
        changed = true;
      }

      for (const item of restItems) {
        await this.changeByKey(item.key, 0);
        changed = true;
      }
    }

    return changed;
  }

  getState(summary) {
    const { shipping, gift, bigGift } = this.config.thresholds;

    if (summary.totalPrice < shipping) return 'below_shipping';
    if (summary.totalPrice < gift) return 'below_gift';
    if (summary.totalPrice < bigGift) return 'below_big_gift';
    if (!summary.hasBigGift) return 'select_big_gift';

    return 'complete';
  }

  getRemainingAmount(target, totalPrice) {
    return Math.max(target - totalPrice, 0);
  }

  formatMoney(cents) {
    const value = this.toNumber(cents) / 100;
    const format = (window.KROWN && KROWN.settings && (KROWN.settings.money_format || KROWN.settings.moneyFormat))
      || (window.Shopify && Shopify.money_format)
      || null;

    if (window.Shopify && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(cents, format).replace('.00', '').replace(',00', '');
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.config.currencyCode,
        maximumFractionDigits: value % 1 === 0 ? 0 : 2
      }).format(value);
    } catch (_) {
      return `${value.toFixed(2)} ${this.config.currencyCode}`;
    }
  }

  replaceAmount(template, cents) {
    const amount = this.formatMoney(cents);
    return String(template)
      .replace('[amount]', amount)
      .replace('{{ amount }}', amount)
      .replace('{{amount}}', amount);
  }

  buildMessage(state, summary) {
    if (state === 'below_shipping') {
      return this.replaceAmount(
        this.config.textSpendShipping,
        this.getRemainingAmount(this.config.thresholds.shipping, summary.totalPrice)
      );
    }

    if (state === 'below_gift') {
      return this.replaceAmount(
        this.config.textSpendGift,
        this.getRemainingAmount(this.config.thresholds.gift, summary.totalPrice)
      );
    }

    if (state === 'below_big_gift') {
      return this.replaceAmount(
        this.config.textSpendBigGift,
        this.getRemainingAmount(this.config.thresholds.bigGift, summary.totalPrice)
      );
    }

    if (state === 'select_big_gift') {
      return this.config.textSelectBigGift;
    }

    return this.config.textComplete;
  }

  setProgressValue(progressEl, value) {
    if (!progressEl) return;
    const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
    progressEl.value = safeValue;
    progressEl.setAttribute('value', safeValue);
    progressEl.dataset.progress = String(Math.round(safeValue));
  }

  render(cart) {
    if (!this.root) return;

    const summary = this.summarizeCart(cart);
    const state = this.getState(summary);
    const isSelectable = state === 'select_big_gift';

    this.root.dataset.state = state;
    this.root.classList.toggle('is-selectable', isSelectable);

    if (this.barEl) {
      this.barEl.classList.toggle('is-clickable', isSelectable);
    }

    const cartTotalPrice = summary.totalPrice;
    const shippingThreshold = this.config.thresholds.shipping || 0;
    const giftThreshold = this.config.thresholds.gift || 0;
    const bigGiftThreshold = this.config.thresholds.bigGift || 0;

    const clampToUnitInterval = value => Math.min(Math.max(value, 0), 1);

    const shippingProgress = shippingThreshold > 0 ? clampToUnitInterval(cartTotalPrice / shippingThreshold) : 1;
    const giftRange = Math.max(giftThreshold - shippingThreshold, 0);
    const giftProgress = giftRange > 0
      ? clampToUnitInterval((cartTotalPrice - shippingThreshold) / giftRange)
      : (cartTotalPrice >= giftThreshold ? 1 : 0);
    const bigGiftRange = Math.max(bigGiftThreshold - giftThreshold, 0);
    const bigGiftProgress = bigGiftRange > 0
      ? clampToUnitInterval((cartTotalPrice - giftThreshold) / bigGiftRange)
      : (cartTotalPrice >= bigGiftThreshold ? 1 : 0);

    const progressValues = {
      shipping: Number((shippingProgress * 100).toFixed(2)),
      gift: Number((giftProgress * 100).toFixed(2)),
      big: Number((bigGiftProgress * 100).toFixed(2))
    };

    this.animateProgress('shipping', this.progressEls.shipping, progressValues.shipping);
    this.animateProgress('gift', this.progressEls.gift, progressValues.gift);
    this.animateProgress('big', this.progressEls.big, progressValues.big);

    const shippingReached = cartTotalPrice >= shippingThreshold;
    const giftReached = cartTotalPrice >= giftThreshold;
    const bigGiftReached = cartTotalPrice >= bigGiftThreshold;

    const stepKeyMap = {
      below_shipping: 'shipping',
      below_gift: 'gift',
      below_big_gift: 'big',
      select_big_gift: 'big'
    };

    const currentStepKey = stepKeyMap[state] || '';

    const stepStateMap = {
      shipping: shippingReached,
      gift: giftReached,
      big: bigGiftReached
    };

    Object.entries(this.stepEls).forEach(([key, el]) => {
      if (!el) return;
      el.classList.toggle('is-active', stepStateMap[key]);
      el.classList.toggle('is-complete', stepStateMap[key]);
      el.classList.toggle('is-current', currentStepKey === key && !stepStateMap[key]);
    });

    if (this.messageEl) {
      this.messageEl.textContent = this.buildMessage(state, summary);
      this.messageEl.classList.toggle('is-clickable', isSelectable);
      this.messageEl.disabled = !isSelectable;
      this.messageEl.setAttribute('aria-disabled', isSelectable ? 'false' : 'true');
    }

    if (this.rewardsEl) {
      this.rewardsEl.hidden = !shippingReached;
    }

    if (this.shippingRewardEl) {
      this.shippingRewardEl.hidden = !shippingReached;
    }

    this.saveRenderCache({
      state,
      message: this.messageEl ? this.messageEl.textContent : '',
      progress: progressValues,
      shippingReached,
      steps: {
        shipping: {
          active: this.stepEls.shipping ? this.stepEls.shipping.classList.contains('is-active') : false,
          complete: this.stepEls.shipping ? this.stepEls.shipping.classList.contains('is-complete') : false,
          current: this.stepEls.shipping ? this.stepEls.shipping.classList.contains('is-current') : false
        },
        gift: {
          active: this.stepEls.gift ? this.stepEls.gift.classList.contains('is-active') : false,
          complete: this.stepEls.gift ? this.stepEls.gift.classList.contains('is-complete') : false,
          current: this.stepEls.gift ? this.stepEls.gift.classList.contains('is-current') : false
        },
        big: {
          active: this.stepEls.big ? this.stepEls.big.classList.contains('is-active') : false,
          complete: this.stepEls.big ? this.stepEls.big.classList.contains('is-complete') : false,
          current: this.stepEls.big ? this.stepEls.big.classList.contains('is-current') : false
        }
      }
    });

    if (summary.hasBigGift) {
      const currentBigGift = summary.bigGiftItems[0];
      const currentVariantId = currentBigGift ? String(currentBigGift.variant_id || currentBigGift.id || '') : '';
      if (currentVariantId) {
        this.highlightSelectedOption(currentVariantId);
      }
      this.closePopup();
    } else if (state !== 'select_big_gift') {
      this.highlightSelectedOption('');
    }
  }

  handleDocumentClick(event) {
    const messageButton = event.target.closest('[data-js-cart-promo-message]');
    if (messageButton && this.root && this.root.dataset.state === 'select_big_gift') {
      event.preventDefault();
      this.openPopup();
      return;
    }

    const closeButton = event.target.closest('[data-js-cart-promo-popup-close]');
    if (closeButton) {
      event.preventDefault();
      this.closePopup();
      return;
    }

    const optionButton = event.target.closest('[data-js-cart-big-gift-option]');
    if (optionButton) {
      event.preventDefault();
      this.highlightSelectedOption(optionButton.dataset.variantId || '');
      return;
    }

    const confirmButton = event.target.closest('[data-js-cart-big-gift-confirm]');
    if (confirmButton) {
      event.preventDefault();
      this.confirmBigGiftSelection();
    }
  }

  handleDocumentKeydown(event) {
    if (event.key === 'Escape' && this.popupEl && !this.popupEl.hidden) {
      this.closePopup();
    }
  }

  highlightSelectedOption(variantId) {
    this.selectedBigGiftVariantId = String(variantId || '');

    this.popupOptionEls.forEach(optionEl => {
      const isSelected = String(optionEl.dataset.variantId || '') === this.selectedBigGiftVariantId;
      optionEl.classList.toggle('is-selected', isSelected);
      optionEl.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (this.confirmButtonEl) {
      this.confirmButtonEl.disabled = !this.selectedBigGiftVariantId;
    }
  }

  openPopup() {
    if (!this.popupEl || !this.popupDialogEl) return;
    this.popupEl.hidden = false;
    document.documentElement.classList.add('cart-promo-popup-open');
    document.body.classList.add('cart-promo-popup-open');
  }

  closePopup() {
    if (!this.popupEl) return;
    this.popupEl.hidden = true;
    document.documentElement.classList.remove('cart-promo-popup-open');
    document.body.classList.remove('cart-promo-popup-open');
  }

  async confirmBigGiftSelection() {
    if (!this.selectedBigGiftVariantId || this.isSyncing) return;

    this.isSyncing = true;

    if (this.confirmButtonEl) {
      this.confirmButtonEl.disabled = true;
      this.confirmButtonEl.setAttribute('aria-busy', 'true');
    }

    try {
      const cart = await this.fetchCart();
      const summary = this.summarizeCart(cart);

      for (const item of summary.bigGiftItems) {
        await this.changeByKey(item.key, 0);
      }

      await this.addVariant(this.selectedBigGiftVariantId, {
        _cart_promo: 'big_free_gift',
        _cart_promo_source: 'cart_drawer_promos'
      });

      this.closePopup();

      if (typeof window.refreshCart === 'function') {
        await window.refreshCart({ openSidebar: false, notify: false });
        if (!this.bindElements()) return;
        this.hydrateFromCache();
      }

      const freshCart = await this.fetchCart();
      this.render(freshCart);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      console.error('[CartDrawerPromos] Unable to add big gift', error);
    } finally {
      if (this.confirmButtonEl) {
        this.confirmButtonEl.removeAttribute('aria-busy');
        this.confirmButtonEl.disabled = !this.selectedBigGiftVariantId;
      }
      this.isSyncing = false;
    }
  }
}

if (!window.__cartDrawerPromos) {
  window.__cartDrawerPromos = new CartDrawerPromos();
}
